import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Wizard from '../../ClientContainer/ClientCreationContainer/wizard';
import * as Yup from 'yup';
import { Form, Formik, useFormikContext } from 'formik';
import InputWrapper from '../../../components/forms/Input/inputWrapper';
import InputImage from '../../../components/forms/Input/inputImage';
import { Input } from '../../../components/forms';
import { Editor } from 'react-draft-wysiwyg';
import {
  ContentState,
  EditorState,
  convertFromHTML,
  convertToRaw,
} from 'draft-js';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  YupEditorMaxLengthValidator,
  YupEditorMinLengthValidator,
} from '../../../utils/validators';
import draftToHtml from 'draftjs-to-html';
import Button from '../../../components/common/Button';
import NewsApi from '../news-api';
import Alert from '../../../components/common/Alert';
import { TIME_TO_REDIRECT } from '../../../utils/constants';
import Select from '../../../components/forms/Select';
import moment from 'moment';

YupEditorMaxLengthValidator();
YupEditorMinLengthValidator();

const MAX_LENGTH_TITLE = 70;
const MAX_LENGTH_NEWS = 500;
const MIN_LENGTH_NEWS = 10;
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const NEWS_LIST_IMAGE_WIDTH = 1600;
const NEWS_LIST_IMAGE_HEIGHT = 1152;
const NEWS_DETAIL_IMAGE_WIDTH = 1600;
const NEWS_DETAIL_IMAGE_HEIGHT = 1200;
// S3 conserva estos prefijos en la URL y el frontend los usa para aplicar el
// nuevo formato sin modificar la presentación de imágenes históricas.
const NEWS_LIST_IMAGE_FILE_PREFIX = 'news-list-v2-';
const NEWS_DETAIL_IMAGE_FILE_PREFIX = 'news-detail-v2-';

const hasImageValue = (value) => {
  if (typeof value === 'string') {
    return Boolean(value.trim());
  }

  return Boolean(value && value.data);
};

const CATEGORIES = [
  { value: 'Cursos' },
  { value: 'Beneficios' },
  { value: 'Talleres' },
  { value: 'Concurso' },
  { value: 'Convocatorias' },
  { value: 'Postulacion' },
  { value: 'Comunidad' },
];

const createNewsSlug = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');

const NewsSlugSync = () => {
  const { values, setFieldValue } = useFormikContext();
  const previousTitleRef = useRef(values.title || '');

  useEffect(() => {
    const previousGeneratedSlug = createNewsSlug(previousTitleRef.current);
    const currentSlug = values.slug || '';
    const shouldSyncSlug = !currentSlug || currentSlug === previousGeneratedSlug;

    if (shouldSyncSlug) {
      const nextSlug = createNewsSlug(values.title);
      if (nextSlug !== currentSlug) {
        setFieldValue('slug', nextSlug, false);
      }
    }

    previousTitleRef.current = values.title || '';
  }, [setFieldValue, values.title, values.slug]);

  return null;
};

const buildEditorStateFromHtml = (html) => {
  if (!html) {
    return EditorState.createEmpty();
  }

  const blocksFromHTML = convertFromHTML(html);
  if (
    !blocksFromHTML.contentBlocks ||
    blocksFromHTML.contentBlocks.length === 0
  ) {
    return EditorState.createEmpty();
  }

  const contentState = ContentState.createFromBlockArray(
    blocksFromHTML.contentBlocks,
    blocksFromHTML.entityMap
  );

  return EditorState.createWithContent(contentState);
};

const NewsForm = ({ mode: modeProp, newsId: newsIdProp }) => {
  const history = useHistory();
  const location = useLocation();

  const derivedMode =
    modeProp || (location && location.state && location.state.mode) || 'create';
  const isEdit = derivedMode === 'edit';
  const currentNewsId = isEdit
    ? newsIdProp || (location && location.state && location.state.id) || null
    : null;

  const [textEditor, setTextEditor] = useState(EditorState.createEmpty());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDraftSubmitting, setIsDraftSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoadingNews, setIsLoadingNews] = useState(true);
  const [news, setNews] = useState([]);
  const [orderedNews, setOrderedNews] = useState([]);
  const [displayOrder, setDisplayOrder] = useState([]);
  const [hasOrderChanges, setHasOrderChanges] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(isEdit);
  const [currentNews, setCurrentNews] = useState(null);

  const submissionModeRef = useRef('publish');

  const schema = Yup.object().shape({
    title: Yup.string()
      .max(
        MAX_LENGTH_TITLE,
        `El titulo no puede superar los ${MAX_LENGTH_TITLE} caracteres`
      )
      .required('Debes ingresar un titulo valido'),
    slug: Yup.string()
      .required('Debes ingresar un slug valido')
      .matches(
        SLUG_PATTERN,
        'Usa solo minusculas, numeros y guiones, sin espacios'
      ),
    description: Yup.string()
      .editorMinLengthValidator(
        MIN_LENGTH_NEWS,
        `La noticia debe contener al menos ${MIN_LENGTH_NEWS} caracteres`
      )
      .editorMaxLengthValidator(
        MAX_LENGTH_NEWS,
        `La noticia no puede superar los ${MAX_LENGTH_NEWS} caracteres`
      )
      .required('Debes ingresar una descripcion valida'),
    image: Yup.mixed().test(
      'required-detail-image',
      'Debes ingresar una imagen de detalle valida',
      hasImageValue
    ),
    largeImage: Yup.mixed().test(
      'required-list-image',
      'Debes ingresar una imagen de listado valida',
      hasImageValue
    ),
    status: Yup.string(),
    category: Yup.string().required('Debes seleccionar una categoria valida'),
    startDate: Yup.string().nullable(true),
    endDate: Yup.string()
      .nullable(true)
      .test(
        'end-date-after-start-date',
        'La fecha de termino debe ser posterior a la fecha de inicio',
        function validateEndDate(value) {
          const { startDate } = this.parent;

          if (!startDate || !value) {
            return true;
          }

          return new Date(value) >= new Date(startDate);
        }
      ),
    displayorder: Yup.number().nullable(true),
  });

  useEffect(() => {
    let cancelled = false;

    if (isLoadingNews) {
      (async () => {
        try {
          const {
            data: { news: fetchedNews },
          } = await NewsApi.fetchNews();

          if (cancelled) {
            return;
          }

          const modifiedNews = (fetchedNews || []).map((n) => {
            const timeAgo = moment().from(n.createdAt, true);
            return { ...n, timeAgo };
          });

          setNews(modifiedNews);
        } catch (error) {
          if (!cancelled) {
            const e =
              (error && error.data && error.data.message) ||
              (error && error.statusText) ||
              'Error fetching news';
            setErrorMessage(e);
          }
        } finally {
          if (!cancelled) {
            setIsLoadingNews(false);
          }
        }
      })();
    }

    return () => {
      cancelled = true;
    };
  }, [isLoadingNews]);

  useEffect(() => {
    if (!isEdit) {
      setIsFormLoading(false);
      return;
    }

    if (!currentNewsId) {
      setIsFormLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const {
          data: { new: fetchedNews },
        } = await NewsApi.fetchNewById(currentNewsId);

        if (cancelled) {
          return;
        }

        setCurrentNews(fetchedNews);
        setTextEditor(buildEditorStateFromHtml(fetchedNews.description));
        submissionModeRef.current =
          fetchedNews.status === 'draft' ? 'draft' : 'publish';
      } catch (error) {
        if (!cancelled) {
          const e =
            (error && error.data && error.data.message) ||
            (error && error.statusText) ||
            'Error fetching news';
          setErrorMessage(e);
        }
      } finally {
        if (!cancelled) {
          setIsFormLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isEdit, currentNewsId]);

  useEffect(() => {
    if (isLoadingNews) {
      return;
    }

    if (isEdit) {
      if (!currentNews) {
        return;
      }

      const baseNews = (news || []).map((item) => ({ ...item }));
      const withoutCurrent = baseNews.filter(
        (item) => item.id !== currentNews.id
      );
      const currentWithMeta = {
        ...currentNews,
        timeAgo:
          currentNews.timeAgo || moment().from(currentNews.createdAt, true),
      };

      if (typeof currentWithMeta.displayorder !== 'number') {
        currentWithMeta.displayorder = currentNews.displayorder || 1;
      }

      const insertionIndex = Math.max(
        0,
        (currentWithMeta.displayorder || 1) - 1
      );
      const truncated = withoutCurrent.slice(0, 3);
      const initialOrder = [...truncated];
      initialOrder.splice(
        Math.min(insertionIndex, initialOrder.length),
        0,
        currentWithMeta
      );

      setDisplayOrder(initialOrder);
      setOrderedNews(truncated);
      setHasOrderChanges(false);
      return;
    }

    const topThree = (news || []).slice(0, 3);
    const initialOrder = [{ id: 'draft-temp', isDraft: true }, ...topThree];
    setOrderedNews(topThree);
    setDisplayOrder(initialOrder);
    setHasOrderChanges(false);
  }, [isLoadingNews, news, isEdit, currentNews]);

  const onDragEndNews = ({ source, destination }) => {
    if (!destination) {
      return;
    }

    if (source.index === destination.index) {
      return;
    }

    setDisplayOrder((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) {
        return prev;
      }

      const updated = Array.from(prev);
      const [moved] = updated.splice(source.index, 1);

      if (!moved) {
        return prev;
      }

      updated.splice(destination.index, 0, moved);

      const hasChanged = updated.some((item, idx) => item !== prev[idx]);

      if (hasChanged) {
        setHasOrderChanges(true);
        return updated;
      }

      return prev;
    });
  };

  const currentEntryId = isEdit && currentNewsId ? currentNewsId : 'draft-temp';

  const buildEffectiveOrder = () => {
    if (Array.isArray(displayOrder) && displayOrder.length > 0) {
      return displayOrder;
    }

    if (isEdit && currentNews) {
      return [
        {
          ...currentNews,
          timeAgo:
            currentNews.timeAgo || moment().from(currentNews.createdAt, true),
        },
        ...orderedNews,
      ];
    }

    return [{ id: 'draft-temp', isDraft: true }, ...(orderedNews || [])];
  };

  const resolveDisplayOrder = () => {
    const effectiveOrder = buildEffectiveOrder();
    const index = effectiveOrder.findIndex(
      (item) => item && item.id === currentEntryId
    );

    return index === -1 ? 1 : index + 1;
  };

  const resolveOrderUpdates = () => {
    const effectiveOrder = buildEffectiveOrder();

    return effectiveOrder
      .map((item, idx) => ({ item, desired: idx + 1 }))
      .filter(({ item }) => item && item.id && item.id !== currentEntryId)
      .filter(({ item, desired }) => Number(item.displayorder) !== desired)
      .map(({ item, desired }) => ({
        id: item.id,
        displayorder: desired,
      }));
  };

  const initialFormikValues = useMemo(() => {
    if (isEdit && currentNews) {
      return {
        title: currentNews.title || '',
        slug: currentNews.slug || createNewsSlug(currentNews.title),
        description: currentNews.description || '',
        image: currentNews.image || '',
        largeImage: currentNews.largeImage || '',
        category: currentNews.category || '',
        startDate: currentNews.startDate || '',
        endDate: currentNews.endDate || '',
        displayorder: currentNews.displayorder || null,
        status: currentNews.status || 'draft',
      };
    }

    return {
      title: '',
      slug: '',
      description: '',
      image: '',
      largeImage: '',
      category: '',
      startDate: '',
      endDate: '',
      displayorder: null,
      status: 'draft',
    };
  }, [isEdit, currentNews]);

  const handleDraftSubmit = (submitForm) => {
    submissionModeRef.current = 'draft';
    submitForm();
  };

  const handlePublishSubmit = (submitForm) => {
    submissionModeRef.current = 'publish';
    submitForm();
  };

  const onSubmit = async (values) => {
    const submissionMode =
      submissionModeRef.current === 'draft' ? 'draft' : 'active';
    const descriptionHtml = draftToHtml(
      convertToRaw(textEditor.getCurrentContent())
    );

    const payload = {
      ...values,
      slug: createNewsSlug(values.slug || values.title),
      description: descriptionHtml,
      status: submissionMode,
      category: values.category,
      startDate: values.startDate || null,
      endDate: values.endDate || null,
      displayorder: resolveDisplayOrder(),
    };

    const orderUpdates = resolveOrderUpdates();
    let succeeded = false;

    try {
      setErrorMessage('');

      if (submissionMode === 'draft') {
        setIsDraftSubmitting(true);
      } else {
        setIsSubmitting(true);
      }

      if (orderUpdates.length > 0) {
        await Promise.all(
          orderUpdates.map(({ id, displayorder }) =>
            NewsApi.updateNews(id, { displayorder })
          )
        );
      }

      if (isEdit && currentNewsId) {
        await NewsApi.updateNews(currentNewsId, payload);
      } else {
        await NewsApi.createNews(payload);
      }

      succeeded = true;
      setHasOrderChanges(false);
    } catch (error) {
      const e =
        (error && error.data && error.data.message) ||
        (error && error.statusText) ||
        'Error al guardar la novedad';
      setErrorMessage(e);
    } finally {
      setIsSubmitting(false);
      setIsDraftSubmitting(false);

      if (succeeded) {
        setTimeout(() => history.push('/novedades'), TIME_TO_REDIRECT);
      }
    }
  };
  if (isEdit && !currentNewsId) {
    return (
      <Wizard title="Editar publicacion">
        <Alert type="error">No se encontro la publicacion a editar.</Alert>
      </Wizard>
    );
  }

  const isFetching = isLoadingNews || (isEdit && isFormLoading);
  const wizardTitle = isEdit ? 'Editar publicacion' : 'Crear nueva publicacion';

  return (
    <Wizard title={wizardTitle}>
      {errorMessage && <Alert type="error">{errorMessage}</Alert>}
      {isFetching ? (
        <div className="wizard__loading">Cargando informacion...</div>
      ) : (
        <Formik
          initialValues={initialFormikValues}
          enableReinitialize
          validationSchema={schema}
          validateOnChange
          onSubmit={onSubmit}
        >
          {({ submitForm, ...props }) => {
            const effectiveOrder = buildEffectiveOrder();
            const draftTitle =
              props.values.title && props.values.title.trim()
                ? props.values.title
                : 'Aqui ira el nombre de tu publicacion';

            const displayList = effectiveOrder
              .slice(0, 4)
              .map((item) => {
                if (!item) {
                  return null;
                }

                if (!isEdit && item.id === 'draft-temp') {
                  return {
                    id: 'draft-temp',
                    title: draftTitle,
                    isDraft: true,
                  };
                }

                if (isEdit && currentNews && item.id === currentEntryId) {
                  return {
                    ...currentNews,
                    title:
                      props.values.title || currentNews.title || draftTitle,
                    timeAgo:
                      currentNews.timeAgo ||
                      moment().from(currentNews.createdAt, true),
                    isEditing: true,
                  };
                }

                return item;
              })
              .filter(Boolean);
            const showSlugWarning =
              isEdit &&
              currentNews &&
              currentNews.status === 'active' &&
              currentNews.slug &&
              props.values.slug &&
              props.values.slug !== currentNews.slug;

            return (
              <Form>
                <NewsSlugSync />
                <div className="wizard__content wizard__content--col">
                  <div className="wizard__steps-container wizard-padding">
                    <InputWrapper
                      variant="big"
                      label="Imagen para listado (banner)"
                      description="Debe medir exactamente 1600 px de ancho por 1152 px de alto. Formato JPG o PNG. Máximo 5 MB."
                      id="largeImage"
                    >
                      <InputImage
                        name="largeImage"
                        id="largeImage"
                        allowedExts={['image/png', 'image/jpg', 'image/jpeg']}
                        expectedWidth={NEWS_LIST_IMAGE_WIDTH}
                        expectedHeight={NEWS_LIST_IMAGE_HEIGHT}
                        fileNamePrefix={NEWS_LIST_IMAGE_FILE_PREFIX}
                      />
                    </InputWrapper>
                    <InputWrapper
                      variant="big"
                      label="Imagen para detalle"
                      description="Debe medir exactamente 1600 px de ancho por 1200 px de alto. Formato JPG o PNG. Máximo 5 MB."
                      id="image"
                    >
                      <InputImage
                        name="image"
                        id="image"
                        allowedExts={['image/png', 'image/jpg', 'image/jpeg']}
                        expectedWidth={NEWS_DETAIL_IMAGE_WIDTH}
                        expectedHeight={NEWS_DETAIL_IMAGE_HEIGHT}
                        fileNamePrefix={NEWS_DETAIL_IMAGE_FILE_PREFIX}
                      />
                    </InputWrapper>
                    <div style={{ width: '100%', marginTop: 12 }}>
                      <div
                        style={{
                          background: '#fff',
                          border: '1px solid #e0e0e0',
                          borderRadius: 12,
                          padding: 20,
                        }}
                      >
                        <h3
                          style={{
                            fontSize: '1.1rem',
                            fontWeight: 700,
                            margin: '0 0 8px 0',
                          }}
                        >
                          Ordenar noticias
                        </h3>
                        <div
                          style={{
                            color: '#222',
                            fontSize: '14px',
                            marginBottom: 12,
                          }}
                        >
                          Arrastra las noticias para cambiar su orden de
                          presentacion
                        </div>
                        <div
                          style={{
                            background: '#f7f8fa',
                            borderRadius: 8,
                            border: '1px solid #e0e0e0',
                            padding: '8px 0',
                            maxHeight: '260px',
                            overflowY: 'auto',
                          }}
                        >
                          <DragDropContext onDragEnd={onDragEndNews}>
                            <Droppable droppableId="newsList">
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.droppableProps}
                                >
                                  {displayList.length > 0 ? (
                                    displayList.map((n, idx) => {
                                      const isCurrent = n.id === currentEntryId;
                                      return (
                                        <Draggable
                                          key={n.id.toString()}
                                          draggableId={n.id.toString()}
                                          index={idx}
                                        >
                                          {(dragProvided, snapshot) => (
                                            <div
                                              ref={dragProvided.innerRef}
                                              {...dragProvided.draggableProps}
                                              {...dragProvided.dragHandleProps}
                                              style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                background: isCurrent
                                                  ? '#e3f2fd'
                                                  : '#fff',
                                                border: isCurrent
                                                  ? '1px solid #90caf9'
                                                  : '1px solid #e0e0e0',
                                                borderRadius: 8,
                                                margin: '8px 12px',
                                                padding: '10px 14px',
                                                boxShadow: snapshot.isDragging
                                                  ? '0 2px 8px rgba(21,101,192,0.10)'
                                                  : 'none',
                                                ...dragProvided.draggableProps
                                                  .style,
                                              }}
                                            >
                                              <svg
                                                width="20"
                                                height="20"
                                                style={{
                                                  marginRight: 12,
                                                  color: isCurrent
                                                    ? '#1565c0'
                                                    : '#bdbdbd',
                                                  flexShrink: 0,
                                                }}
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                              >
                                                <rect
                                                  x="5"
                                                  y="6"
                                                  width="2"
                                                  height="2"
                                                  rx="1"
                                                  fill={
                                                    isCurrent
                                                      ? '#1565c0'
                                                      : '#bdbdbd'
                                                  }
                                                />
                                                <rect
                                                  x="5"
                                                  y="10"
                                                  width="2"
                                                  height="2"
                                                  rx="1"
                                                  fill={
                                                    isCurrent
                                                      ? '#1565c0'
                                                      : '#bdbdbd'
                                                  }
                                                />
                                                <rect
                                                  x="5"
                                                  y="14"
                                                  width="2"
                                                  height="2"
                                                  rx="1"
                                                  fill={
                                                    isCurrent
                                                      ? '#1565c0'
                                                      : '#bdbdbd'
                                                  }
                                                />
                                                <rect
                                                  x="13"
                                                  y="6"
                                                  width="2"
                                                  height="2"
                                                  rx="1"
                                                  fill={
                                                    isCurrent
                                                      ? '#1565c0'
                                                      : '#bdbdbd'
                                                  }
                                                />
                                                <rect
                                                  x="13"
                                                  y="10"
                                                  width="2"
                                                  height="2"
                                                  rx="1"
                                                  fill={
                                                    isCurrent
                                                      ? '#1565c0'
                                                      : '#bdbdbd'
                                                  }
                                                />
                                                <rect
                                                  x="13"
                                                  y="14"
                                                  width="2"
                                                  height="2"
                                                  rx="1"
                                                  fill={
                                                    isCurrent
                                                      ? '#1565c0'
                                                      : '#bdbdbd'
                                                  }
                                                />
                                              </svg>
                                              <span
                                                style={{
                                                  fontWeight: 600,
                                                  fontSize: 15,
                                                  color: '#222',
                                                  flex: 1,
                                                }}
                                              >
                                                {n.title}
                                              </span>
                                              {isCurrent ? (
                                                <span
                                                  style={{
                                                    fontWeight: 600,
                                                    fontSize: 13,
                                                    color: '#1565c0',
                                                    background: '#e8f0ff',
                                                    borderRadius: 6,
                                                    padding: '2px 8px',
                                                    marginLeft: 12,
                                                  }}
                                                >
                                                  {isEdit
                                                    ? `En edicion - #${idx + 1}`
                                                    : `Nueva - #${idx + 1}`}
                                                </span>
                                              ) : (
                                                <span
                                                  style={{
                                                    fontWeight: 600,
                                                    fontSize: 13,
                                                    color: '#888',
                                                    background: '#f2f7fe',
                                                    borderRadius: 6,
                                                    padding: '2px 8px',
                                                    marginLeft: 12,
                                                  }}
                                                >
                                                  #{idx + 1}
                                                </span>
                                              )}
                                            </div>
                                          )}
                                        </Draggable>
                                      );
                                    })
                                  ) : (
                                    <div
                                      style={{
                                        textAlign: 'center',
                                        color: '#888',
                                        padding: '24px 0',
                                      }}
                                    >
                                      No hay noticias disponibles
                                    </div>
                                  )}
                                  {provided.placeholder}
                                </div>
                              )}
                            </Droppable>
                          </DragDropContext>
                        </div>
                        <div
                          style={{
                            background: '#fffde7',
                            borderRadius: 8,
                            padding: '10px 12px',
                            fontSize: 13,
                            color: '#b28704',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            marginTop: 12,
                          }}
                        >
                          <svg
                            width="18"
                            height="18"
                            fill="none"
                            style={{ marginRight: 6 }}
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10 2a8 8 0 100 16 8 8 0 000-16zm.75 12.25h-1.5v-1.5h1.5v1.5zm0-3h-1.5V5.75h1.5v3.5z"
                              fill="#b28704"
                            />
                          </svg>
                          <span>
                            <b>Tip:</b> Arrastra las noticias desde el icono de
                            lineas para cambiar su orden
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="wizard__form--center grid wizard-padding">
                    <InputWrapper
                      variant="big"
                      label="Nombre de la publicacion"
                      id="title"
                    >
                      <Input name="title" label="" id="title" />
                      <span className="char-counter">
                        {(props.values.title || '').length}/ {MAX_LENGTH_TITLE}
                      </span>
                    </InputWrapper>
                    <InputWrapper
                      variant="big"
                      label="Slug"
                      id="slug"
                    >
                      <Input
                        name="slug"
                        label=""
                        id="slug"
                        placeholder="nombre-de-la-novedad"
                      />
                      {showSlugWarning ? (
                        <Alert type="warning">
                          Cambiar el slug modifica la URL publica. Se creara una redireccion 301 desde el slug anterior.
                        </Alert>
                      ) : null}
                    </InputWrapper>
                    <InputWrapper variant="big" label="Categoria" id="category">
                      <Select
                        label="Selecciona Categoria"
                        name="category"
                        options={[]}
                        advancedOptions={CATEGORIES}
                        isRequired
                      />
                    </InputWrapper>
                    <InputWrapper
                      variant="big"
                      label="Fecha de Inicio"
                      id="startDate"
                    >
                      <Input name="startDate" id="startDate" type="date" />
                    </InputWrapper>
                    <InputWrapper
                      variant="big"
                      label="Fecha de término"
                      id="endDate"
                    >
                      <Input
                        name="endDate"
                        id="endDate"
                        type="date"
                        min={props.values.startDate || undefined}
                      />
                    </InputWrapper>
                    <InputWrapper
                      label="Descripción"
                      id="description"
                      name="description"
                      variant="big"
                    >
                      <>
                        <Editor
                          id="description"
                          name="description"
                          wrapperClassName="editor__wrapper"
                          toolbarClassName="editor__toolbar"
                          editorState={textEditor}
                          onEditorStateChange={setTextEditor}
                          onBlur={() => {
                            props.setFieldTouched('description', true);
                            props.setFieldValue(
                              'description',
                              draftToHtml(
                                convertToRaw(textEditor.getCurrentContent())
                              )
                            );
                          }}
                        />
                        <div className="editor__error">
                          {props.touched.description &&
                            props.errors.description && (
                              <span className=" error-message">
                                {props.errors.description}
                              </span>
                            )}
                          <span className="char-counter">
                            {
                              textEditor.getCurrentContent().getPlainText('')
                                .length
                            }
                            /{MAX_LENGTH_NEWS}
                          </span>
                        </div>
                      </>
                    </InputWrapper>
                  </div>
                </div>
                <div className="wizard__lower-action">
                  <Button
                    secondary
                    loading={isDraftSubmitting}
                    onClick={() => handleDraftSubmit(submitForm)}
                  >
                    Guardar
                  </Button>
                  <Button
                    loading={isSubmitting}
                    onClick={() => handlePublishSubmit(submitForm)}
                  >
                    Publicar
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      )}
    </Wizard>
  );
};

export default NewsForm;
